import { IUNCService } from '@/services/iUNCService';
import { ICacheService } from '@/services/iCacheService';
import {
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  RedirectRequestHandler,
  DefaultCrypto,
  LocalStorageBackend,
} from '@openid/appauth';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from '@/util/constants';

/**
 * @description UNC SSO service implementation using @openid/appauth-js
 */
export class UNCService implements IUNCService {
  private cacheService: ICacheService;
  private config: AuthorizationServiceConfiguration;
  private clientId: string;
  private redirectUri: string;

  constructor() {
    this.cacheService = ObjectFactory.getCacheService();
    const clientId = process.env.VUE_APP_UNC_CLIENT_ID;
    const tenantId = process.env.VUE_APP_UNC_TENANT_ID;
    if (!clientId) {
      throw new Error('VUE_APP_UNC_CLIENT_ID environment variable is required');
    }
    this.clientId = clientId;
    if (!tenantId) {
      throw new Error('VUE_APP_UNC_TENANT_ID environment variable is required');
    }
    this.redirectUri = `${window.location.origin}/callback`;

    if (!this.clientId || !tenantId) {
      throw new Error(
        'UNC SSO configuration missing. Check environment variables.',
      );
    }

    this.config = new AuthorizationServiceConfiguration({
      authorization_endpoint: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`,
      token_endpoint: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`,
      revocation_endpoint: `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`,
    });
  }

  public isUNCSubdomain(): boolean {
    const host = window.location.host;
    const subdomain = host.split('.')[0];
    return subdomain.includes('unc'); // check if subdomain is 'unc'
  }

  public hasUNCCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  }

  public async initiateLogin(): Promise<void> {
    try {
      // Step 1: Generate PKCE
      const crypto = new DefaultCrypto();
      const codeVerifier = crypto.generateRandom(128);
      const codeChallenge = await crypto.deriveChallenge(codeVerifier);
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);

      // Step 2: Create Authorization Request
      const authRequest = new AuthorizationRequest(
        {
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          scope: 'openid profile email',
          response_type: 'code', // Use 'code' for authorization code flow
          state: crypto.generateRandom(32), // Generate a random state for CSRF protection
          extras: {
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
          },
        },
        undefined,
        true, // PKCE enabled
      );

      // Step 3: Use persistent handler
      const handler = new RedirectRequestHandler(new LocalStorageBackend());
      const authUrl = `${this.config.authorizationEndpoint}?` +
        new URLSearchParams({
          client_id: this.clientId,
          response_type: 'code',
          redirect_uri: this.redirectUri,
          scope: 'openid profile email',
          state: authRequest.state,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
        });
      window.location.href = authUrl;
      // After this, browser will redirect to the auth provider (e.g., Microsoft)
    } catch (error) {
      throw error;
    }
  }

  public async handleCallback(clientId: string): Promise<void> {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const state = urlParams.get('state');
      if (error) {
        const desc = urlParams.get('error_description') || error;
        throw new Error(`OAuth2 error: ${desc}`);
      }

      if (!code) {
        throw new Error(`Missing 'code' or 'state' in callback URL.`);
      }

      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
      if (!codeVerifier) {
        throw new Error('Missing PKCE code verifier in session storage.');
      }

      const tokenEndpoint = this.config.tokenEndpoint;
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.redirectUri,
        client_id: this.clientId,
        code_verifier: codeVerifier,
      });

      const tokenResponse = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`Token exchange failed: ${tokenResponse.status} - ${errorText}`);
      }

      const data = await tokenResponse.json();
      const accessToken = data.access_token;

      if (!accessToken) {
        throw new Error('Access token not found in token response.');
      }

      sessionStorage.removeItem('pkce_code_verifier');
      await this.exchangeUNCTokenForAppToken(data, clientId);

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.href = '/requests';
    } catch (err) {
      throw err;
    }
  }

  public async exchangeUNCTokenForAppToken(
    tokenData: any, clientId: string,
  ): Promise<void> {
    try {
      const serverUrl = process.env.VUE_APP_MWS_URL;
      const apiUrl = `${serverUrl}${MedTransGoConstants.HTTP_URL_UNC_SSO}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId,
          ...tokenData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(
          `UNC token exchange failed: ${response.status} - ${errorData}`,
        );
      }

      const data = await response.json();
      if (!data.success || !data.data) {
        throw new Error('Invalid response from UNC token exchange');
      }

      this.cacheService.saveValue(
        MedTransGoConstants.FIELD_NAME_TOKEN,
        data.data,
      );
    } catch (error) {
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      // Clear local cache first
      this.cacheService.clearCache();
      // Redirect to Microsoft logout endpoint
      const tenantId = process.env.VUE_APP_UNC_TENANT_ID;
      const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
      // Redirect to logout URL
      window.location.href = logoutUrl;
    } catch (error) {
      // Fallback - just go to home page
      window.location.href = '/';
      throw error;
    }
  }
}
